import React, { useEffect, useState } from 'react';
import API from '../services/api';
import { useNavigate } from 'react-router-dom';
import { getUser } from '../utils/authUtil';
import RecommendationCard from '../components/recommendations/RecommendationCard';
import ProgressRing from '../components/ui/ProgressRing';
import Icon from '../components/ui/Icon';
import EmptyState from '../components/ui/EmptyState';
import ErrorState from '../components/ui/ErrorState';
import { SkeletonHero, SkeletonCard } from '../components/ui/Skeleton';
import OnboardingTour from '../components/onboarding/OnboardingTour';
import AvatarIntro from '../components/onboarding/AvatarIntro';
import { readinessLevelColor, readinessLevelLabel, scoreColor } from '../components/diagnostic/diagnosticHelpers';
import { hasCompletedOnboarding, markOnboardingComplete, isGenuinelyNewStudent } from '../utils/onboarding';
import PlayerHeader from '../components/player/PlayerHeader';
import SkillCard from '../components/player/SkillCard';
import TodayMissions from '../components/player/TodayMissions';
import AchievementCard from '../components/player/AchievementCard';
import { useStudent } from '../components/student/StudentContext';
import { REPLAY_TOUR_KEY } from './StudentSettings';
import './StudentPortal.css';

const TREND_COPY = {
  improving: { icon: 'trend-up', text: 'Trending up', color: 'var(--z-success)' },
  declining: { icon: 'trend-down', text: 'Trending down', color: 'var(--z-decline)' },
  stable: { icon: 'trend-flat', text: 'Holding steady', color: 'var(--z-muted)' },
  insufficient_data: null,
};

const ONBOARDING_STEPS = [
  { targetId: 'ob-readiness', title: 'Your placement journey', body: 'This is your current placement-readiness — a live signal, not a fixed score. It grows more confident as you take more assessments.' },
  { targetId: 'ob-recommendation', title: 'Your next best move', body: 'This is Zaptude\'s single most useful suggestion for what to do next, with the evidence behind it one tap away.' },
  { targetId: 'ob-missions', title: 'Today\'s missions', body: 'Small daily goals. Finish them to keep your streak going and your XP growing.' },
  { targetId: 'ob-diagnostic', title: 'Your skills', body: 'Zaptude tracks your mastery topic by topic, based on real evidence from your attempts — never a guess.' },
];

// Home = the Player Hub. Hierarchy: identity → readiness → today's next move → missions → skills → recent wins → activity.
const StudentPortal = () => {
  const navigate = useNavigate();
  const user = getUser();
  const { player, setAvatarId, openProfile } = useStudent();
  const [data, setData] = useState(null);
  const [diagnostic, setDiagnostic] = useState(null);
  const [topRecommendation, setTopRecommendation] = useState(null);
  const [recLoaded, setRecLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showTour, setShowTour] = useState(false);
  const [showAvatarIntro, setShowAvatarIntro] = useState(false);

  const fetchDashboard = async () => {
    setLoading(true); setError(null);
    try {
      const res = await API.get('/portal/dashboard');
      setData(res.data);
      let replay = false;
      try { replay = sessionStorage.getItem(REPLAY_TOUR_KEY) === '1'; sessionStorage.removeItem(REPLAY_TOUR_KEY); } catch { /* ignore */ }
      if (replay) setShowTour(true);
      else if (isGenuinelyNewStudent(res.data) && !hasCompletedOnboarding(user?.id)) setShowAvatarIntro(true);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
    // Best-effort, non-blocking — a failure here never breaks the rest of Home.
    API.get('/portal/diagnostic-intelligence').then(res => setDiagnostic(res.data)).catch(() => setDiagnostic(null));
    API.get('/portal/recommendations')
      .then(res => setTopRecommendation(res.data.recommendations?.[0] || null))
      .catch(() => setTopRecommendation(null))
      .finally(() => setRecLoaded(true));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchDashboard(); }, []);

  const finishAvatarIntro = async (avatarId) => {
    if (avatarId) {
      try {
        const res = await API.patch('/portal/avatar', { avatar_id: avatarId });
        setAvatarId(res.data.avatar_id);
      } catch (err) { console.error(err); }
    }
    setShowAvatarIntro(false);
    setShowTour(true);
  };
  const finishTour = () => { markOnboardingComplete(user?.id); setShowTour(false); };

  if (loading) {
    return (
      <div className="z-page home-page" role="status" aria-label="Loading your home">
        <SkeletonHero />
        <SkeletonCard lines={2} />
        <SkeletonCard lines={3} />
      </div>
    );
  }

  if (error || !data || !data.student) {
    return <div className="z-page home-page"><ErrorState error={error} onRetry={fetchDashboard} /></div>;
  }

  const { student, recentAttempts, overallStats, readiness } = data;
  const trend = readiness?.available ? TREND_COPY[readiness.trend] : null;
  const topics = diagnostic?.topics || [];
  const skillTopics = topics.slice(0, 4);
  const heldAchievements = (player?.achievements || []).filter(a => a.held);
  const isNew = overallStats?.tests_taken === 0;

  return (
    <div className="z-page home-page">
      {showAvatarIntro && <AvatarIntro name={student.name} onDone={finishAvatarIntro} />}
      {showTour && <OnboardingTour steps={ONBOARDING_STEPS} onFinish={finishTour} />}

      {/* ── Player identity: Avatar → Level → XP → Streak ── */}
      <PlayerHeader name={student.name} avatarId={player?.avatar_id} progress={player} onAvatarClick={openProfile} />

      {/* ── Placement readiness, in plain language ── */}
      <div id="ob-readiness" className="z-surface home-readiness">
        {readiness?.available ? (
          <>
            <ProgressRing value={Math.round(readiness.score)} color={readinessLevelColor(readiness.level)} label="READY" size={88} strokeWidth={8} />
            <div className="home-readiness-info">
              <span className="home-readiness-eyebrow">Placement readiness</span>
              <span className="home-readiness-level" style={{ color: readinessLevelColor(readiness.level) }}>{readinessLevelLabel(readiness.level)}</span>
              <span className="home-readiness-conf">{readiness.confidence} confidence · {readiness.considered} attempt{readiness.considered === 1 ? '' : 's'}</span>
              {trend && <span className="home-readiness-trend" style={{ color: trend.color }}><Icon name={trend.icon} size={14} /> {trend.text}</span>}
            </div>
          </>
        ) : (
          <>
            <ProgressRing value={0} max={100} color="var(--z-neutral)" label="—" size={88} strokeWidth={8} />
            <div className="home-readiness-info">
              <span className="home-readiness-eyebrow">Placement readiness</span>
              <span className="home-readiness-level home-readiness-level--muted">Not enough evidence yet</span>
              <span className="home-readiness-conf">{readiness?.message || 'Complete a few challenges to discover your readiness.'}</span>
            </div>
          </>
        )}
      </div>

      {/* ── Today's next move ── */}
      <section id="ob-recommendation" className="z-section" aria-labelledby="home-next">
        <h2 className="z-section-title" id="home-next">Today's next move</h2>
        {topRecommendation ? (
          <RecommendationCard rec={topRecommendation} variant="hero" />
        ) : recLoaded ? (
          <EmptyState
            icon="rocket" compact
            title={isNew ? 'Your first challenge is waiting' : 'Keep the momentum going'}
            body={isNew ? 'Start a quick session — Zaptude will learn what you\'re good at.' : 'Jump into a challenge to keep building your skills.'}
            action={{ label: 'Start a challenge', onClick: () => navigate('/student/practice') }}
          />
        ) : <SkeletonCard lines={2} />}
      </section>

      {/* ── Missions ── */}
      {player?.missions && (
        <section id="ob-missions" className="z-section" aria-labelledby="home-missions">
          <h2 className="z-section-title" id="home-missions">Today's mission</h2>
          <TodayMissions missions={player.missions} personalizedJourneyEligible={player.personalized_journey_eligible} />
        </section>
      )}

      {/* ── Skills ── */}
      <section id="ob-diagnostic" className="z-section" aria-labelledby="home-skills">
        <div className="z-section-head">
          <h2 className="z-section-title" id="home-skills">Skills</h2>
          <button type="button" className="z-section-link" onClick={() => navigate('/student/analytics')}>See all</button>
        </div>
        {skillTopics.length > 0 ? (
          <div className="home-skill-list">
            {skillTopics.map(t => (
              <SkillCard key={t.topic_id} topicName={t.topic_name} state={t.state} masteryPct={t.evidence?.accuracy_confidence_adjusted_pct} />
            ))}
          </div>
        ) : (
          <EmptyState icon="skill" compact tone="neutral" title="Your skill map is empty" body="Answer a few questions and it will start filling in." />
        )}
      </section>

      {/* ── Recent wins ── */}
      {heldAchievements.length > 0 && (
        <section className="z-section" aria-labelledby="home-wins">
          <h2 className="z-section-title" id="home-wins">Recent wins</h2>
          <div className="home-achievement-row">
            {heldAchievements.map(a => <AchievementCard key={a.id} achievement={a} />)}
          </div>
        </section>
      )}

      {/* ── Recent activity (condensed — not a full table) ── */}
      <section className="z-section" aria-labelledby="home-activity">
        <h2 className="z-section-title" id="home-activity">Recent activity</h2>
        {recentAttempts.length === 0 ? (
          <EmptyState icon="growth" compact tone="neutral" title="No activity yet" body="Your completed challenges and assessments will show up here." />
        ) : (
          <div className="home-activity-list">
            {recentAttempts.slice(0, 3).map((a, i) => {
              const pct = a.total_marks > 0 ? (a.score / a.total_marks) * 100 : 0;
              return (
                <button type="button" key={i} className="z-surface z-surface--tap home-activity-row" onClick={() => navigate('/student/practice')}>
                  <span className="home-activity-info">
                    <span className="home-activity-name">{a.test_name}</span>
                    <span className="home-activity-date">{new Date(a.attempted_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</span>
                  </span>
                  <span className="home-activity-score" style={{ color: scoreColor(pct) }}>{a.score}/{a.total_marks}</span>
                </button>
              );
            })}
          </div>
        )}
        {overallStats?.tests_taken > 0 && (
          <p className="home-footnote">{overallStats.tests_taken} assessment{overallStats.tests_taken === 1 ? '' : 's'} · {parseFloat(overallStats.avg_score || 0).toFixed(0)}% average score</p>
        )}
      </section>
    </div>
  );
};

export default StudentPortal;
